'use client';

import { useEffect, useMemo, useRef } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  DomainTreePanel as DesignSystemDomainTreePanel,
  DomainTreeNodeType,
  IconButton,
  Spinner,
  Tooltip,
  type DomainTreeNode,
} from '@terra/ui-design-system';
import { PanelLeftCloseIcon } from '@terra/ui-icons';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';
import type { WebPageWithSignedScreenshotReturnDto } from '@terra/shared-contracts-web-pages';
import { useAppTargetId } from '../../store/projectStore';
import { useTreeExpansion } from '../../store/domainTreeStore';
import { useRequiredDomainTree } from '../../contexts/SharedDomainTreeContext';
import { useTabScopedDomainTree } from '../../store/tab/domainTreeTabScopedFilterStore';
import {
  ASSET_TYPE,
  type AssetType,
} from '../../store/tab/assetsTabScopedFilterStore';
import { useDomainTreePanelCounts } from '../../hooks/layout/useDomainTreePanelCounts';
import { useDomainTreePanelSelection } from '../../hooks/layout/useDomainTreePanelSelection';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';

const getExpandedNodeIds = (nodeMap: Map<string, DomainTreeNode>) => {
  return Array.from(nodeMap.entries())
    .filter(([, node]) => node.isExpanded)
    .map(([nodeId]) => nodeId);
};

type DomainTreePanelProps = {
  shouldApplyAppLevel?: boolean;
  isExecutionsTab?: boolean;
  assetTypeFilter?: AssetType;
  onEndpointSelect?: (endpoint: {
    id: string;
    path: string;
    method?: string;
    data?: EndpointReturnDto;
  }) => void;
  onWebPageSelect?: (webpage: {
    id: string;
    url: string;
    data?: WebPageWithSignedScreenshotReturnDto;
  }) => void;
  selectionMode?: 'multiple' | 'single';
  onMinimize?: () => void;
  countsLifecycleState?: LifecycleStateFilterDto;
};

export function DomainTreePanel({
  onEndpointSelect,
  onWebPageSelect,
  selectionMode = 'multiple',
  onMinimize,
  shouldApplyAppLevel,
  isExecutionsTab,
  assetTypeFilter,
  countsLifecycleState,
}: DomainTreePanelProps) {
  const appTargetId = useAppTargetId();
  const { selectedNodes, setSelectedNodes } = useTabScopedDomainTree();
  const { expandedNodes, setExpandedNodes } = useTreeExpansion(appTargetId);
  const {
    domainTree,
    isLoading: isDomainTreeLoading,
    isFetching: isDomainTreeFetching,
    refetch: refetchDomainTree,
  } = useRequiredDomainTree();
  const isNodeSelectable = useMemo(() => {
    return (node: DomainTreeNode): boolean => {
      if (!assetTypeFilter || assetTypeFilter === ASSET_TYPE.ALL) {
        return true;
      }

      if (assetTypeFilter === ASSET_TYPE.WEB_PAGES) {
        return node.nodeType !== DomainTreeNodeType.Endpoint;
      }

      return node.nodeType !== DomainTreeNodeType.Webpage;
    };
  }, [assetTypeFilter]);
  const prevAssetTypeFilterRef = useRef<AssetType | undefined>(undefined);

  useEffect(() => {
    if (!assetTypeFilter) {
      prevAssetTypeFilterRef.current = undefined;

      return;
    }

    if (
      prevAssetTypeFilterRef.current &&
      prevAssetTypeFilterRef.current !== assetTypeFilter
    ) {
      setSelectedNodes([]);
    }

    prevAssetTypeFilterRef.current = assetTypeFilter;
  }, [assetTypeFilter, setSelectedNodes]);
  useEffect(() => {
    if (!domainTree) {
      return;
    }

    const hasStaleSelection = selectedNodes.some((nodeId) => {
      const node = domainTree.nodeMap.get(nodeId);

      return !node || !isNodeSelectable(node);
    });

    if (!hasStaleSelection) {
      return;
    }

    setSelectedNodes(
      selectedNodes.filter((nodeId) => {
        const node = domainTree.nodeMap.get(nodeId);

        return node !== undefined && isNodeSelectable(node);
      }),
    );
  }, [domainTree, isNodeSelectable, selectedNodes, setSelectedNodes]);
  const { nodeIdToCountMap, isRefreshing, handleRefresh } =
    useDomainTreePanelCounts({
      appTargetId,
      shouldApplyAppLevel,
      isExecutionsTab,
      domainTree,
      isDomainTreeFetching,
      refetchDomainTree,
      lifecycleState: countsLifecycleState,
    });
  const { handleDomainSelectionChange } = useDomainTreePanelSelection({
    domainTree,
    selectedNodes,
    setSelectedNodes,
    selectionMode,
    onEndpointSelect,
    onWebPageSelect,
  });
  const effectiveExpandedNodes = useMemo(() => {
    if (!domainTree) {
      return expandedNodes ?? [];
    }

    return expandedNodes ?? getExpandedNodeIds(domainTree.nodeMap);
  }, [domainTree, expandedNodes]);
  const renderHeader = () => (
    <HStack
      h="8"
      px="2"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      bg="bg.canvas"
      justifyContent="flex-end"
      alignItems="center"
      flexShrink={0}
    >
      {onMinimize && (
        <Tooltip title="Minimize panel" positioning={{ placement: 'bottom' }}>
          <IconButton
            variant="link"
            size="xs"
            onClick={onMinimize}
            aria-label="Minimize panel"
            _hover={{
              color: 'fg.default',
              bg: 'transparent',
            }}
            p="1"
          >
            <PanelLeftCloseIcon size={12} />
          </IconButton>
        </Tooltip>
      )}
    </HStack>
  );

  if (isDomainTreeLoading && !domainTree) {
    return (
      <Box
        h="full"
        w="full"
        overflow="hidden"
        bg="bg.canvas"
        display="flex"
        flexDirection="column"
      >
        {renderHeader()}
        <Box
          flex="1"
          overflow="hidden"
          display="flex"
          alignItems="center"
          justifyContent="center"
          aria-busy="true"
        >
          <Spinner size="md" />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      h="full"
      w="full"
      overflow="hidden"
      bg="bg.canvas"
      display="flex"
      flexDirection="column"
    >
      {renderHeader()}
      <Box flex="1" overflow="hidden">
        <DesignSystemDomainTreePanel
          domainTree={domainTree}
          selectedDomains={selectedNodes}
          onDomainSelectionChange={handleDomainSelectionChange}
          nodeIdToCountMap={nodeIdToCountMap}
          fullWidth={true}
          selectionMode={selectionMode}
          expandedNodes={effectiveExpandedNodes}
          onExpandedNodesChange={setExpandedNodes}
          disableCollapsible
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          isLoading={isDomainTreeLoading}
          listLifecycleState={countsLifecycleState}
          isNodeSelectable={isNodeSelectable}
        />
      </Box>
    </Box>
  );
}
